/**
 * MuiQuickView - Order Production Quick View Page
 *
 * ERP-style page for tracking order production progress:
 * - Left: Order list with filters
 * - Center: Section progress for selected order
 * - Right: Info pane (missing materials, status overview)
 */
import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  Typography,
  Stack,
  Breadcrumbs,
  Link,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Grid,
  LinearProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Card,
  CardContent,
  Badge,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from "@mui/material"
import HomeIcon from "@mui/icons-material/Home"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import SearchIcon from "@mui/icons-material/Search"
import RefreshIcon from "@mui/icons-material/Refresh"
import FilterListIcon from "@mui/icons-material/FilterList"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import FactoryIcon from "@mui/icons-material/Factory"
import InventoryIcon from "@mui/icons-material/Inventory"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import PendingIcon from "@mui/icons-material/Pending"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import WarningIcon from "@mui/icons-material/Warning"
import ErrorIcon from "@mui/icons-material/Error"
import AssignmentIcon from "@mui/icons-material/Assignment"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import PersonIcon from "@mui/icons-material/Person"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import VisibilityIcon from "@mui/icons-material/Visibility"
import AnalyticsIcon from "@mui/icons-material/Analytics"
import TaskAltIcon from "@mui/icons-material/TaskAlt"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import CloseIcon from "@mui/icons-material/Close"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import PriorityHighIcon from "@mui/icons-material/PriorityHigh"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts"

import { getOrders, getAllTasks, getTypes, getMaterials } from "helpers/api_helper"

const MuiQuickView = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  document.title = t("Quick View") + " | FlexFlow ERP"

  // State
  const [orders, setOrders] = useState([])
  const [tasks, setTasks] = useState([])
  const [taskTypes, setTaskTypes] = useState([])
  const [materials, setMaterials] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedOrder, setSelectedOrder] = useState(null)

  // Filters
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("ALL")
  const [showFilters, setShowFilters] = useState(false)
  const [analyticsExpanded, setAnalyticsExpanded] = useState(true)
  const [sectionModalOpen, setSectionModalOpen] = useState(false)
  const [selectedSection, setSelectedSection] = useState(null)
  const [orderSectionModalOpen, setOrderSectionModalOpen] = useState(false)
  const [selectedOrderSection, setSelectedOrderSection] = useState(null)

  // Pagination
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(15)
  const [lastUpdated, setLastUpdated] = useState(null)

  // Fetch data with cache-busting
  const fetchData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true)
    try {
      // Add timestamp to bust cache
      const timestamp = Date.now()
      const [ordersRes, tasksRes, typesRes, materialsRes] = await Promise.all([
        getOrders(),
        getAllTasks(),
        getTypes(),
        getMaterials()
      ])
      setOrders(ordersRes || [])
      setTasks(tasksRes || [])
      setTaskTypes(typesRes || [])
      setMaterials(materialsRes || [])
      setLastUpdated(new Date())
    } catch (err) {
      console.error("Error fetching data:", err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial fetch
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Refresh on window focus (when user comes back to this tab)
  useEffect(() => {
    const handleFocus = () => {
      fetchData(false) // Don't show loading spinner on focus refresh
    }
    window.addEventListener("focus", handleFocus)
    return () => window.removeEventListener("focus", handleFocus)
  }, [fetchData])

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData(false) // Silent refresh
    }, 30000)
    return () => clearInterval(interval)
  }, [fetchData])

  // Filter orders
  const filteredOrders = useMemo(() => {
    let result = orders

    // Exclude INVOICED orders (they are already complete)
    result = result.filter(o => o.status !== "INVOICED")

    // Status filter
    if (statusFilter !== "ALL") {
      result = result.filter(o => o.status === statusFilter)
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(o =>
        String(o.id).includes(term) ||
        (o.name || "").toLowerCase().includes(term) ||
        (o.label || "").toLowerCase().includes(term)
      )
    }

    return result
  }, [orders, statusFilter, searchTerm])

  // Get tasks for selected order
  const orderTasks = useMemo(() => {
    if (!selectedOrder) return []
    return tasks.filter(t => t.orderref === selectedOrder.id)
  }, [tasks, selectedOrder])

  // Group tasks by section (type)
  const sectionProgress = useMemo(() => {
    if (!selectedOrder || orderTasks.length === 0) return []

    const sections = {}
    orderTasks.forEach(task => {
      const sectionName = task.type || "Unknown"
      if (!sections[sectionName]) {
        sections[sectionName] = {
          name: sectionName,
          tasks: [],
          totalTasks: 0,
          completedTasks: 0,
          pendingTasks: 0,
          startedTasks: 0,
          missingMaterials: []
        }
      }
      sections[sectionName].tasks.push(task)
      sections[sectionName].totalTasks++

      if (task.status === "FINISHED") {
        sections[sectionName].completedTasks++
      } else if (task.status === "PENDING") {
        sections[sectionName].pendingTasks++
      } else {
        sections[sectionName].startedTasks++
      }

      // Check for missing materials
      try {
        const taskMaterials = JSON.parse(task.materials || "[]")
        taskMaterials.forEach(mat => {
          const materialData = materials.find(m => m.id === mat.id)
          if (materialData) {
            const required = parseFloat(mat.quantity) || 0
            const available = parseFloat(materialData.stock) || 0
            if (available < required) {
              sections[sectionName].missingMaterials.push({
                ...mat,
                available,
                required,
                shortage: required - available
              })
            }
          }
        })
      } catch (e) {}
    })

    return Object.values(sections).sort((a, b) => {
      // Sort by completion status
      const aComplete = a.completedTasks === a.totalTasks
      const bComplete = b.completedTasks === b.totalTasks
      if (aComplete !== bComplete) return aComplete ? 1 : -1
      return a.name.localeCompare(b.name)
    })
  }, [orderTasks, materials, selectedOrder])

  // Calculate overall progress
  const overallProgress = useMemo(() => {
    if (orderTasks.length === 0) return { total: 0, completed: 0, pending: 0, started: 0, percent: 0 }
    const total = orderTasks.length
    const completed = orderTasks.filter(t => t.status === "FINISHED").length
    const pending = orderTasks.filter(t => t.status === "PENDING").length
    const started = total - completed - pending
    return {
      total,
      completed,
      pending,
      started,
      percent: Math.round((completed / total) * 100)
    }
  }, [orderTasks])

  // Get all missing materials for the order
  const allMissingMaterials = useMemo(() => {
    const missing = []
    sectionProgress.forEach(section => {
      section.missingMaterials.forEach(mat => {
        const existing = missing.find(m => m.id === mat.id)
        if (existing) {
          existing.required += mat.required
          existing.shortage += mat.shortage
        } else {
          missing.push({ ...mat })
        }
      })
    })
    return missing
  }, [sectionProgress])

  // Check if all sections have started
  const allSectionsStarted = useMemo(() => {
    if (sectionProgress.length === 0) return false
    return sectionProgress.every(s => s.startedTasks > 0 || s.completedTasks > 0)
  }, [sectionProgress])

  // Summary Analytics for the panel
  const summaryAnalytics = useMemo(() => {
    // Only consider orders that are not INVOICED (already filtered)
    const productionOrders = orders.filter(o => o.status !== "INVOICED")

    // Count tasks per status
    const totalTasks = tasks.length
    const completedTasks = tasks.filter(t => t.status === "FINISHED").length
    const pendingTasks = tasks.filter(t => t.status === "PENDING").length
    const activeTasks = totalTasks - completedTasks - pendingTasks

    // Orders with tasks
    const orderIdsWithTasks = [...new Set(tasks.map(t => t.orderref))]
    const ordersInProduction = productionOrders.filter(o => orderIdsWithTasks.includes(o.id)).length

    // Calculate average progress
    let totalProgress = 0
    let ordersWithProgress = 0
    productionOrders.forEach(order => {
      const orderTaskList = tasks.filter(t => t.orderref === order.id)
      if (orderTaskList.length > 0) {
        const completed = orderTaskList.filter(t => t.status === "FINISHED").length
        totalProgress += (completed / orderTaskList.length) * 100
        ordersWithProgress++
      }
    })
    const avgProgress = ordersWithProgress > 0 ? Math.round(totalProgress / ordersWithProgress) : 0

    // Orders by status
    const statusCounts = {}
    productionOrders.forEach(o => {
      statusCounts[o.status] = (statusCounts[o.status] || 0) + 1
    })

    // Tasks by section
    const sectionCounts = {}
    tasks.forEach(t => {
      const section = t.type || "Unknown"
      if (!sectionCounts[section]) {
        sectionCounts[section] = { total: 0, completed: 0, pending: 0, active: 0 }
      }
      sectionCounts[section].total++
      if (t.status === "FINISHED") {
        sectionCounts[section].completed++
      } else if (t.status === "PENDING") {
        sectionCounts[section].pending++
      } else {
        sectionCounts[section].active++
      }
    })

    // Section chart data (bar chart)
    const sectionChartData = Object.entries(sectionCounts)
      .map(([name, data]) => ({
        name: name.length > 12 ? name.substring(0, 12) + "..." : name,
        fullName: name,
        completed: data.completed,
        active: data.active,
        pending: data.pending,
        total: data.total,
        progress: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0,
      }))
      .sort((a, b) => b.total - a.total)

    // Order status pie chart data
    const statusColors = {
      "PENDING-STOCK": "#f59e0b",
      "STOCK-OK": "#3b82f6",
      "READY-TO-INVOICE": "#6366f1",
      "ON SHIPPING": "#ec4899",
      "OFFER": "#6b7280",
    }
    const orderStatusChartData = Object.entries(statusCounts).map(([status, count]) => ({
      name: status,
      value: count,
      color: statusColors[status] || "#94a3b8",
    }))

    // Task status pie chart data
    const taskStatusChartData = [
      { name: "Completed", value: completedTasks, color: "#16a34a" },
      { name: "Active", value: activeTasks, color: "#3b82f6" },
      { name: "Pending", value: pendingTasks, color: "#f59e0b" },
    ].filter(d => d.value > 0)

    // Orders due soon (within 7 days)
    const today = new Date()
    const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
    const urgentOrders = productionOrders.filter(o => {
      if (!o.expected_delivery_date) return false
      const dueDate = new Date(o.expected_delivery_date)
      return dueDate <= weekFromNow && dueDate >= today
    }).length

    const overdueOrders = productionOrders.filter(o => {
      if (!o.expected_delivery_date) return false
      const dueDate = new Date(o.expected_delivery_date)
      return dueDate < today
    }).length

    // Busiest sections (by pending + active tasks)
    const busiestSections = Object.entries(sectionCounts)
      .map(([name, data]) => ({
        name,
        workload: data.pending + data.active,
        total: data.total,
        completed: data.completed,
      }))
      .sort((a, b) => b.workload - a.workload)
      .slice(0, 3)

    return {
      totalOrders: productionOrders.length,
      ordersInProduction,
      totalTasks,
      completedTasks,
      pendingTasks,
      activeTasks,
      avgProgress,
      statusCounts,
      sectionCounts,
      completionRate: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0,
      sectionChartData,
      orderStatusChartData,
      taskStatusChartData,
      urgentOrders,
      overdueOrders,
      busiestSections,
    }
  }, [orders, tasks])

  // Selected section detailed data for modal
  const selectedSectionData = useMemo(() => {
    if (!selectedSection) return null

    const sectionTasks = tasks.filter(t => t.type === selectedSection)
    const completed = sectionTasks.filter(t => t.status === "FINISHED").length
    const pending = sectionTasks.filter(t => t.status === "PENDING").length
    const active = sectionTasks.length - completed - pending
    const progress = sectionTasks.length > 0 ? Math.round((completed / sectionTasks.length) * 100) : 0

    // Group tasks by order
    const tasksByOrder = {}
    sectionTasks.forEach(task => {
      const orderId = task.orderref
      if (!tasksByOrder[orderId]) {
        const order = orders.find(o => o.id === orderId)
        tasksByOrder[orderId] = {
          orderId,
          orderName: order?.name || `Order #${orderId}`,
          orderLabel: order?.label || "",
          tasks: [],
          completed: 0,
          total: 0
        }
      }
      tasksByOrder[orderId].tasks.push(task)
      tasksByOrder[orderId].total++
      if (task.status === "FINISHED") {
        tasksByOrder[orderId].completed++
      }
    })

    // Sort by completion (incomplete first)
    const ordersList = Object.values(tasksByOrder).sort((a, b) => {
      const aComplete = a.completed === a.total
      const bComplete = b.completed === b.total
      if (aComplete !== bComplete) return aComplete ? 1 : -1
      return b.total - a.total
    })

    return {
      name: selectedSection,
      totalTasks: sectionTasks.length,
      completed,
      pending,
      active,
      progress,
      ordersList,
      totalOrders: ordersList.length
    }
  }, [selectedSection, tasks, orders])

  // Handle section chip click
  const handleSectionClick = (sectionName) => {
    setSelectedSection(sectionName)
    setSectionModalOpen(true)
  }

  // Handle order section card click (for selected order)
  const handleOrderSectionClick = (sectionName) => {
    setSelectedOrderSection(sectionName)
    setOrderSectionModalOpen(true)
  }

  // Get tasks for selected order and section (for modal)
  const orderSectionTasks = useMemo(() => {
    if (!selectedOrder || !selectedOrderSection) return []
    return tasks.filter(t => t.orderref === selectedOrder.id && t.type === selectedOrderSection)
  }, [selectedOrder, selectedOrderSection, tasks])

  // Status options (INVOICED excluded - those orders are already complete)
  const statusOptions = [
    { value: "ALL", label: t("All Orders") },
    { value: "PENDING-STOCK", label: t("Pending Stock") },
    { value: "STOCK-OK", label: t("Stock OK") },
    { value: "READY-TO-INVOICE", label: t("Ready to Invoice") },
    { value: "ON SHIPPING", label: t("On Shipping") },
    { value: "OFFER", label: t("Offer") },
  ]

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      "PENDING-STOCK": { bg: "#fef3c7", color: "#d97706" },
      "STOCK-OK": { bg: "#dbeafe", color: "#3b82f6" },
      "READY-TO-INVOICE": { bg: "#e0e7ff", color: "#4338ca" },
      "INVOICED": { bg: "#dcfce7", color: "#16a34a" },
      "ON SHIPPING": { bg: "#fce7f3", color: "#db2777" },
      "SHIPPED": { bg: "#d1fae5", color: "#059669" },
      "OFFER": { bg: "#f3f4f6", color: "#6b7280" },
    }
    return colors[status] || { bg: "#f3f4f6", color: "#6b7280" }
  }

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-"
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric" })
    } catch {
      return dateStr
    }
  }

  // Handle order selection
  const handleOrderSelect = (order) => {
    setSelectedOrder(selectedOrder?.id === order.id ? null : order)
  }

  return (
    <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5 } }}>
              <Link
                component="button"
                underline="hover"
                sx={{ display: "flex", alignItems: "center", color: "#64748b", fontSize: "0.875rem" }}
                onClick={() => navigate("/")}
              >
                <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t("Home")}
              </Link>
              <Typography color="#1e293b" fontWeight={500} fontSize="0.875rem">
                {t("Quick View")}
              </Typography>
            </Breadcrumbs>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={<InventoryIcon sx={{ fontSize: 16 }} />}
              label={`${filteredOrders.length} ${t("Orders")}`}
              size="small"
              sx={{ bgcolor: "#e0e7ff", color: "#4338ca", fontWeight: 500 }}
            />
            {lastUpdated && (
              <Typography variant="caption" color="#94a3b8" sx={{ fontSize: "0.7rem" }}>
                {t("Updated")}: {lastUpdated.toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </Typography>
            )}
            <IconButton size="small" onClick={() => setShowFilters(!showFilters)} sx={{ color: showFilters ? "#3b82f6" : "#64748b" }}>
              <FilterListIcon fontSize="small" />
            </IconButton>
            <Tooltip title={t("Refresh Data")}>
              <IconButton size="small" onClick={() => fetchData(true)} disabled={loading} sx={{ color: loading ? "#94a3b8" : "#3b82f6" }}>
                <RefreshIcon fontSize="small" sx={{ animation: loading ? "spin 1s linear infinite" : "none", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Filters */}
        <Collapse in={showFilters}>
          <Stack direction="row" spacing={2} sx={{ mt: 2, pt: 2, borderTop: "1px solid #e2e8f0" }} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              placeholder={t("Search orders...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t("Status")}</InputLabel>
              <Select
                value={statusFilter}
                label={t("Status")}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Collapse>
      </Paper>

      {/* Analytics Panel */}
      <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        {/* Toggle Header */}
        <Box
          onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            bgcolor: analyticsExpanded ? "#f8fafc" : "transparent",
            "&:hover": { bgcolor: "#f8fafc" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AnalyticsIcon sx={{ fontSize: 18, color: "#64748b" }} />
            <Typography variant="body2" fontWeight={500} color="#475569">
              {t("Production Analytics")}
            </Typography>
          </Stack>
          {analyticsExpanded ? <ExpandLessIcon sx={{ color: "#64748b" }} /> : <ExpandMoreIcon sx={{ color: "#64748b" }} />}
        </Box>

        {/* Collapsible Content */}
        <Collapse in={analyticsExpanded}>
          <Box sx={{ p: 2, borderTop: "1px solid #e2e8f0" }}>
            {/* Stats Row */}
            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              {/* Total Orders */}
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", minWidth: 120, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6", width: 36, height: 36 }}>
                    <InventoryIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1e293b">
                      {summaryAnalytics.totalOrders}
                    </Typography>
                    <Typography variant="caption" color="#64748b">
                      {t("Orders")}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Total Tasks */}
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", minWidth: 120, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "#e0e7ff", color: "#4338ca", width: 36, height: 36 }}>
                    <TaskAltIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1e293b">
                      {summaryAnalytics.totalTasks}
                    </Typography>
                    <Typography variant="caption" color="#64748b">
                      {t("Total Tasks")}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Completed Tasks */}
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", minWidth: 120, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "#dcfce7", color: "#16a34a", width: 36, height: 36 }}>
                    <CheckCircleIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1e293b">
                      {summaryAnalytics.completedTasks}
                    </Typography>
                    <Typography variant="caption" color="#64748b">
                      {t("Completed")}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Avg Progress */}
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", minWidth: 120, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "#fce7f3", color: "#db2777", width: 36, height: 36 }}>
                    <TrendingUpIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1e293b">
                      {summaryAnalytics.avgProgress}%
                    </Typography>
                    <Typography variant="caption" color="#64748b">
                      {t("Avg Progress")}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Urgent Orders */}
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: summaryAnalytics.urgentOrders > 0 ? "1px solid #fcd34d" : "1px solid #e2e8f0", bgcolor: summaryAnalytics.urgentOrders > 0 ? "#fffbeb" : "#fff", minWidth: 120, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "#fef3c7", color: "#d97706", width: 36, height: 36 }}>
                    <AccessTimeIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color={summaryAnalytics.urgentOrders > 0 ? "#d97706" : "#1e293b"}>
                      {summaryAnalytics.urgentOrders}
                    </Typography>
                    <Typography variant="caption" color="#64748b">
                      {t("Due This Week")}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Overdue Orders */}
              {summaryAnalytics.overdueOrders > 0 && (
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #fca5a5", bgcolor: "#fef2f2", minWidth: 120, flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: "#fee2e2", color: "#dc2626", width: 36, height: 36 }}>
                      <PriorityHighIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={700} color="#dc2626">
                        {summaryAnalytics.overdueOrders}
                      </Typography>
                      <Typography variant="caption" color="#64748b">
                        {t("Overdue")}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}
            </Stack>

            {/* Charts Row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Section Load Bar Chart */}
              <Grid item xs={12} md={8}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
                    {t("Section Workload")}
                  </Typography>
                  <Box sx={{ height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={summaryAnalytics.sectionChartData} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11, fill: "#64748b" }} />
                        <RechartsTooltip
                          contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
                          formatter={(value, name) => [value, name === "completed" ? t("Completed") : name === "active" ? t("Active") : t("Pending")]}
                          labelFormatter={(label) => {
                            const item = summaryAnalytics.sectionChartData.find(d => d.name === label)
                            return item?.fullName || label
                          }}
                        />
                        <Bar dataKey="completed" stackId="a" fill="#16a34a" name="completed" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="active" stackId="a" fill="#3b82f6" name="active" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="pending" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              {/* Task Status Pie Chart */}
              <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0", height: "100%" }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1 }}>
                    {t("Task Status")}
                  </Typography>
                  <Box sx={{ height: 180 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={summaryAnalytics.taskStatusChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={65}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {summaryAnalytics.taskStatusChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                          formatter={(value, name) => [value, t(name)]}
                        />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          formatter={(value) => <span style={{ color: "#475569", fontSize: 11 }}>{t(value)}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Bottom Row - Status & Sections */}
            <Grid container spacing={2}>
              {/* Busiest Sections */}
              <Grid item xs={12} md={4}>
                <Typography variant="caption" fontWeight={600} color="#475569" sx={{ mb: 1, display: "block", textTransform: "uppercase" }}>
                  {t("Busiest Sections")}
                </Typography>
                <Stack spacing={1}>
                  {summaryAnalytics.busiestSections.map((section, idx) => (
                    <Paper
                      key={section.name}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: "1px solid #e2e8f0",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "#f8fafc" },
                      }}
                      onClick={() => handleSectionClick(section.name)}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{ bgcolor: idx === 0 ? "#fee2e2" : idx === 1 ? "#fef3c7" : "#e0e7ff", color: idx === 0 ? "#dc2626" : idx === 1 ? "#d97706" : "#4338ca", width: 28, height: 28, fontSize: 12 }}>
                            {idx + 1}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500} color="#1e293b" noWrap sx={{ maxWidth: 100 }}>
                            {section.name}
                          </Typography>
                        </Stack>
                        <Chip
                          label={`${section.workload} ${t("pending")}`}
                          size="small"
                          sx={{ bgcolor: "#fef3c7", color: "#d97706", fontWeight: 500, fontSize: "0.65rem", height: 20 }}
                        />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Grid>

              {/* Orders by Status */}
              <Grid item xs={12} md={4}>
                <Typography variant="caption" fontWeight={600} color="#475569" sx={{ mb: 1, display: "block", textTransform: "uppercase" }}>
                  {t("Orders by Status")}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Object.entries(summaryAnalytics.statusCounts).map(([status, count]) => {
                    const statusColor = getStatusColor(status)
                    return (
                      <Chip
                        key={status}
                        label={`${status}: ${count}`}
                        size="small"
                        sx={{
                          bgcolor: statusColor.bg,
                          color: statusColor.color,
                          fontWeight: 500,
                          fontSize: "0.7rem",
                        }}
                      />
                    )
                  })}
                </Stack>
              </Grid>

              {/* Tasks by Section (clickable) */}
              <Grid item xs={12} md={4}>
                <Typography variant="caption" fontWeight={600} color="#475569" sx={{ mb: 1, display: "block", textTransform: "uppercase" }}>
                  {t("Sections")} ({t("click for details")})
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Object.entries(summaryAnalytics.sectionCounts).map(([section, data]) => {
                    const progress = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0
                    return (
                      <Chip
                        key={section}
                        label={`${section}: ${progress}%`}
                        size="small"
                        clickable
                        onClick={() => handleSectionClick(section)}
                        sx={{
                          bgcolor: progress === 100 ? "#dcfce7" : progress > 50 ? "#dbeafe" : "#fef3c7",
                          color: progress === 100 ? "#16a34a" : progress > 50 ? "#3b82f6" : "#d97706",
                          fontWeight: 500,
                          fontSize: "0.7rem",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: 1,
                            bgcolor: progress === 100 ? "#bbf7d0" : progress > 50 ? "#bfdbfe" : "#fde68a",
                          },
                          transition: "all 0.2s ease",
                        }}
                      />
                    )
                  })}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />}

      {/* Main Content */}
      <Grid container spacing={1.5}>
        {/* Left: Orders List */}
        <Grid item xs={12} md={selectedOrder ? 4 : 12}>
          <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <TableContainer sx={{ maxHeight: selectedOrder ? "calc(100vh - 520px)" : "calc(100vh - 480px)" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& th": { bgcolor: "#f8fafc", fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase", py: 1.5 } }}>
                    <TableCell>#</TableCell>
                    <TableCell>{t("Customer")}</TableCell>
                    {!selectedOrder && <TableCell>{t("Label")}</TableCell>}
                    <TableCell>{t("Date")}</TableCell>
                    <TableCell>{t("Status")}</TableCell>
                    {!selectedOrder && <TableCell align="center">{t("Tasks")}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((order) => {
                      const orderTaskCount = tasks.filter(t => t.orderref === order.id).length
                      const isSelected = selectedOrder?.id === order.id
                      const statusColor = getStatusColor(order.status)

                      return (
                        <TableRow
                          key={order.id}
                          hover
                          selected={isSelected}
                          onClick={() => handleOrderSelect(order)}
                          sx={{
                            cursor: "pointer",
                            bgcolor: isSelected ? "#eff6ff" : "transparent",
                            "&:hover": { bgcolor: isSelected ? "#eff6ff" : "#f8fafc" },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} color="#3b82f6">
                              #{order.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500} color="#1e293b" noWrap sx={{ maxWidth: 150 }}>
                              {order.name}
                            </Typography>
                          </TableCell>
                          {!selectedOrder && (
                            <TableCell>
                              <Typography variant="body2" color="#64748b" noWrap sx={{ maxWidth: 120 }}>
                                {order.label || "-"}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography variant="caption" color="#64748b">
                              {formatDate(order.expected_delivery_date)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={order.status}
                              size="small"
                              sx={{
                                bgcolor: statusColor.bg,
                                color: statusColor.color,
                                fontWeight: 500,
                                fontSize: "0.7rem",
                                height: 22,
                              }}
                            />
                          </TableCell>
                          {!selectedOrder && (
                            <TableCell align="center">
                              <Chip
                                label={orderTaskCount}
                                size="small"
                                sx={{ bgcolor: orderTaskCount > 0 ? "#dbeafe" : "#f3f4f6", color: orderTaskCount > 0 ? "#3b82f6" : "#9ca3af", fontWeight: 600, height: 22, minWidth: 32 }}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    })}
                  {filteredOrders.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={selectedOrder ? 4 : 6} align="center" sx={{ py: 4 }}>
                        <Typography color="#94a3b8">{t("No orders found")}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredOrders.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
              rowsPerPageOptions={[10, 15, 25, 50]}
              sx={{ borderTop: "1px solid #e2e8f0" }}
            />
          </Paper>
        </Grid>

        {/* Center: Section Progress */}
        {selectedOrder && (
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0", height: "calc(100vh - 520px)", overflow: "auto" }}>
              {/* Order Header */}
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "#3b82f6", width: 48, height: 48 }}>
                  <AssignmentIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={700} color="#1e293b">
                    {t("Order")} #{selectedOrder.id}
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    {selectedOrder.name}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => navigate(`/order/${selectedOrder.id}`)}>
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Stack>

              {/* Overall Progress */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#475569">
                    {t("Overall Progress")}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color={overallProgress.percent >= 80 ? "#16a34a" : overallProgress.percent >= 50 ? "#3b82f6" : "#d97706"}>
                    {overallProgress.percent}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={overallProgress.percent}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#e2e8f0",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 4,
                      bgcolor: overallProgress.percent >= 80 ? "#16a34a" : overallProgress.percent >= 50 ? "#3b82f6" : "#d97706",
                    },
                  }}
                />
                <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
                  <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label={`${overallProgress.completed} ${t("Done")}`} size="small" sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 500 }} />
                  <Chip icon={<PlayArrowIcon sx={{ fontSize: 14 }} />} label={`${overallProgress.started} ${t("Active")}`} size="small" sx={{ bgcolor: "#dbeafe", color: "#3b82f6", fontWeight: 500 }} />
                  <Chip icon={<PendingIcon sx={{ fontSize: 14 }} />} label={`${overallProgress.pending} ${t("Pending")}`} size="small" sx={{ bgcolor: "#fef3c7", color: "#d97706", fontWeight: 500 }} />
                </Stack>
              </Paper>

              {/* Section Cards */}
              <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
                {t("Sections Progress")} ({sectionProgress.length})
              </Typography>

              {sectionProgress.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {t("No tasks found for this order")}
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  {sectionProgress.map((section) => {
                    const progress = section.totalTasks > 0 ? Math.round((section.completedTasks / section.totalTasks) * 100) : 0
                    const isComplete = progress === 100
                    const hasStarted = section.startedTasks > 0 || section.completedTasks > 0
                    const hasMissing = section.missingMaterials.length > 0

                    return (
                      <Card
                        key={section.name}
                        elevation={0}
                        onClick={() => handleOrderSectionClick(section.name)}
                        sx={{
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: isComplete ? "#16a34a" : hasMissing ? "#ef4444" : "#e2e8f0",
                          bgcolor: isComplete ? "#f0fdf4" : hasMissing ? "#fef2f2" : "#fff",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: 2,
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Badge
                              badgeContent={hasMissing ? "!" : null}
                              color="error"
                              sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16 } }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: isComplete ? "#dcfce7" : hasStarted ? "#dbeafe" : "#fef3c7",
                                  color: isComplete ? "#16a34a" : hasStarted ? "#3b82f6" : "#d97706",
                                  width: 36,
                                  height: 36,
                                }}
                              >
                                <FactoryIcon sx={{ fontSize: 18 }} />
                              </Avatar>
                            </Badge>
                            <Box sx={{ flex: 1 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
                                  {section.name}
                                </Typography>
                                <Typography variant="caption" fontWeight={600} color={isComplete ? "#16a34a" : "#64748b"}>
                                  {progress}%
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                  mt: 0.5,
                                  height: 4,
                                  borderRadius: 2,
                                  bgcolor: "#e2e8f0",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 2,
                                    bgcolor: isComplete ? "#16a34a" : "#3b82f6",
                                  },
                                }}
                              />
                              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Typography variant="caption" color="#16a34a">✓ {section.completedTasks}</Typography>
                                <Typography variant="caption" color="#3b82f6">▶ {section.startedTasks}</Typography>
                                <Typography variant="caption" color="#d97706">◷ {section.pendingTasks}</Typography>
                                {hasMissing && (
                                  <Tooltip title={section.missingMaterials.map(m => m.name).join(", ")}>
                                    <Typography variant="caption" color="#ef4444" sx={{ cursor: "help" }}>
                                      ⚠ {section.missingMaterials.length} {t("missing")}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Stack>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    )
                  })}
                </Stack>
              )}
            </Paper>
          </Grid>
        )}

        {/* Right: Info Pane */}
        {selectedOrder && (
          <Grid item xs={12} md={3}>
            <Stack spacing={1.5}>
              {/* Order Info */}
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
                <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
                  {t("Order Info")}
                </Typography>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: "#f1f5f9", color: "#64748b", width: 32, height: 32 }}>
                      <PersonIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="#94a3b8">{t("Customer")}</Typography>
                      <Typography variant="body2" fontWeight={500} color="#1e293b">{selectedOrder.name}</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: "#f1f5f9", color: "#64748b", width: 32, height: 32 }}>
                      <CalendarTodayIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="#94a3b8">{t("Delivery Date")}</Typography>
                      <Typography variant="body2" fontWeight={500} color="#1e293b">{formatDate(selectedOrder.expected_delivery_date)}</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: "#f1f5f9", color: "#64748b", width: 32, height: 32 }}>
                      <LocalShippingIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="#94a3b8">{t("Status")}</Typography>
                      <Chip
                        label={selectedOrder.status}
                        size="small"
                        sx={{ ...getStatusColor(selectedOrder.status), fontWeight: 500, height: 22, mt: 0.5 }}
                      />
                    </Box>
                  </Stack>
                </Stack>
              </Paper>

              {/* Section Status */}
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
                <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
                  {t("Section Status")}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {allSectionsStarted ? (
                    <>
                      <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 20 }} />
                      <Typography variant="body2" color="#16a34a" fontWeight={500}>
                        {t("All sections started")}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <WarningIcon sx={{ color: "#d97706", fontSize: 20 }} />
                      <Typography variant="body2" color="#d97706" fontWeight={500}>
                        {t("Some sections not started")}
                      </Typography>
                    </>
                  )}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  {sectionProgress.map(s => {
                    const hasStarted = s.startedTasks > 0 || s.completedTasks > 0
                    const isComplete = s.completedTasks === s.totalTasks
                    return (
                      <Chip
                        key={s.name}
                        label={s.name}
                        size="small"
                        sx={{
                          bgcolor: isComplete ? "#dcfce7" : hasStarted ? "#dbeafe" : "#fef3c7",
                          color: isComplete ? "#16a34a" : hasStarted ? "#3b82f6" : "#d97706",
                          fontWeight: 500,
                          fontSize: "0.7rem",
                        }}
                      />
                    )
                  })}
                </Stack>
              </Paper>

              {/* Missing Materials */}
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: allMissingMaterials.length > 0 ? "1px solid #fca5a5" : "1px solid #e2e8f0", bgcolor: allMissingMaterials.length > 0 ? "#fef2f2" : "#fff" }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  {allMissingMaterials.length > 0 ? (
                    <ErrorIcon sx={{ color: "#ef4444", fontSize: 20 }} />
                  ) : (
                    <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 20 }} />
                  )}
                  <Typography variant="subtitle2" fontWeight={600} color={allMissingMaterials.length > 0 ? "#ef4444" : "#16a34a"}>
                    {allMissingMaterials.length > 0 ? t("Missing Materials") : t("Materials OK")}
                  </Typography>
                </Stack>

                {allMissingMaterials.length > 0 ? (
                  <Stack spacing={1} sx={{ maxHeight: 200, overflow: "auto" }}>
                    {allMissingMaterials.map((mat, idx) => (
                      <Paper key={idx} elevation={0} sx={{ p: 1.5, bgcolor: "#fff", borderRadius: 1.5, border: "1px solid #fecaca" }}>
                        <Typography variant="body2" fontWeight={500} color="#1e293b" noWrap>
                          {mat.name}
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="#64748b">
                            {t("Required")}: {mat.required}
                          </Typography>
                          <Typography variant="caption" color="#ef4444" fontWeight={600}>
                            {t("Short")}: {mat.shortage.toFixed(1)}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="#64748b">
                    {t("All materials available for production")}
                  </Typography>
                )}
              </Paper>

              {/* Products in Order */}
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
                <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
                  {t("Products")} ({(() => { try { return JSON.parse(selectedOrder.products || "[]").length } catch { return 0 } })()})
                </Typography>
                <Stack spacing={1} sx={{ maxHeight: 150, overflow: "auto" }}>
                  {(() => {
                    try {
                      const products = JSON.parse(selectedOrder.products || "[]")
                      return products.slice(0, 5).map((p, idx) => (
                        <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="#1e293b" noWrap sx={{ maxWidth: 150 }}>
                            {p.name}
                          </Typography>
                          <Chip label={`x${p.quantity}`} size="small" sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 500, height: 20, fontSize: "0.7rem" }} />
                        </Stack>
                      ))
                    } catch {
                      return <Typography variant="body2" color="#94a3b8">{t("No products")}</Typography>
                    }
                  })()}
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        )}
      </Grid>

      {/* Section Detail Modal */}
      <Dialog
        open={sectionModalOpen}
        onClose={() => setSectionModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: "#e0e7ff", color: "#4338ca", width: 44, height: 44 }}>
                <FactoryIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700} color="#1e293b">
                  {selectedSectionData?.name}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  {t("Section Overview")}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={() => setSectionModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedSectionData && (
            <>
              {/* Stats Row */}
              <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", minWidth: 100, flex: 1 }}>
                    <Typography variant="h5" fontWeight={700} color="#1e293b">{selectedSectionData.totalTasks}</Typography>
                    <Typography variant="caption" color="#64748b">{t("Total Tasks")}</Typography>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", minWidth: 100, flex: 1 }}>
                    <Typography variant="h5" fontWeight={700} color="#16a34a">{selectedSectionData.completed}</Typography>
                    <Typography variant="caption" color="#64748b">{t("Completed")}</Typography>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", minWidth: 100, flex: 1 }}>
                    <Typography variant="h5" fontWeight={700} color="#3b82f6">{selectedSectionData.active}</Typography>
                    <Typography variant="caption" color="#64748b">{t("Active")}</Typography>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", minWidth: 100, flex: 1 }}>
                    <Typography variant="h5" fontWeight={700} color="#d97706">{selectedSectionData.pending}</Typography>
                    <Typography variant="caption" color="#64748b">{t("Pending")}</Typography>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", minWidth: 100, flex: 1 }}>
                    <Typography variant="h5" fontWeight={700} color={selectedSectionData.progress >= 80 ? "#16a34a" : selectedSectionData.progress >= 50 ? "#3b82f6" : "#d97706"}>
                      {selectedSectionData.progress}%
                    </Typography>
                    <Typography variant="caption" color="#64748b">{t("Progress")}</Typography>
                  </Paper>
                </Stack>

                {/* Progress Bar */}
                <Box sx={{ mt: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={selectedSectionData.progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#e2e8f0",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 4,
                        bgcolor: selectedSectionData.progress >= 80 ? "#16a34a" : selectedSectionData.progress >= 50 ? "#3b82f6" : "#d97706",
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Orders List */}
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ mb: 1.5 }}>
                  {t("Orders with tasks in this section")} ({selectedSectionData.totalOrders})
                </Typography>
                <TableContainer sx={{ maxHeight: 350 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ "& th": { bgcolor: "#f8fafc", fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" } }}>
                        <TableCell>{t("Order")}</TableCell>
                        <TableCell>{t("Customer")}</TableCell>
                        <TableCell>{t("Label")}</TableCell>
                        <TableCell align="center">{t("Tasks")}</TableCell>
                        <TableCell align="center">{t("Progress")}</TableCell>
                        <TableCell align="center">{t("Status")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedSectionData.ordersList.map((orderData) => {
                        const orderProgress = orderData.total > 0 ? Math.round((orderData.completed / orderData.total) * 100) : 0
                        const isComplete = orderProgress === 100
                        return (
                          <TableRow
                            key={orderData.orderId}
                            hover
                            onClick={() => {
                              const order = orders.find(o => o.id === orderData.orderId)
                              if (order) {
                                setSectionModalOpen(false)
                                setSelectedOrder(order)
                              }
                            }}
                            sx={{
                              cursor: "pointer",
                              bgcolor: isComplete ? "#f0fdf4" : "transparent",
                              "&:hover": { bgcolor: isComplete ? "#dcfce7" : "#f8fafc" },
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} color="#3b82f6">
                                #{orderData.orderId}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500} color="#1e293b" noWrap sx={{ maxWidth: 150 }}>
                                {orderData.orderName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="#64748b" noWrap sx={{ maxWidth: 100 }}>
                                {orderData.orderLabel || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${orderData.completed}/${orderData.total}`}
                                size="small"
                                sx={{
                                  bgcolor: isComplete ? "#dcfce7" : "#dbeafe",
                                  color: isComplete ? "#16a34a" : "#3b82f6",
                                  fontWeight: 600,
                                  height: 22,
                                  fontSize: "0.7rem",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <LinearProgress
                                  variant="determinate"
                                  value={orderProgress}
                                  sx={{
                                    width: 60,
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: "#e2e8f0",
                                    "& .MuiLinearProgress-bar": {
                                      borderRadius: 3,
                                      bgcolor: isComplete ? "#16a34a" : "#3b82f6",
                                    },
                                  }}
                                />
                                <Typography variant="caption" fontWeight={500} color={isComplete ? "#16a34a" : "#64748b"}>
                                  {orderProgress}%
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              {isComplete ? (
                                <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 20 }} />
                              ) : orderData.completed > 0 ? (
                                <PlayArrowIcon sx={{ color: "#3b82f6", fontSize: 20 }} />
                              ) : (
                                <PendingIcon sx={{ color: "#d97706", fontSize: 20 }} />
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Order Section Tasks Modal */}
      <Dialog
        open={orderSectionModalOpen}
        onClose={() => setOrderSectionModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6", width: 44, height: 44 }}>
                <FactoryIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700} color="#1e293b">
                  {selectedOrderSection}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  {t("Order")} #{selectedOrder?.id} - {selectedOrder?.name}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={() => {
                  setOrderSectionModalOpen(false)
                  navigate(`/single/${encodeURIComponent(selectedOrderSection)}?order=${selectedOrder?.id}`)
                }}
                sx={{
                  bgcolor: "#3b82f6",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#2563eb" },
                }}
              >
                {t("Go to Section")}
              </Button>
              <IconButton onClick={() => setOrderSectionModalOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {/* Stats Summary */}
          <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <Stack direction="row" spacing={2}>
              <Chip
                icon={<TaskAltIcon sx={{ fontSize: 16 }} />}
                label={`${orderSectionTasks.length} ${t("Tasks")}`}
                sx={{ bgcolor: "#e0e7ff", color: "#4338ca", fontWeight: 500 }}
              />
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                label={`${orderSectionTasks.filter(t => t.status === "FINISHED").length} ${t("Completed")}`}
                sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 500 }}
              />
              <Chip
                icon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
                label={`${orderSectionTasks.filter(t => t.status !== "FINISHED" && t.status !== "PENDING").length} ${t("Active")}`}
                sx={{ bgcolor: "#dbeafe", color: "#3b82f6", fontWeight: 500 }}
              />
              <Chip
                icon={<PendingIcon sx={{ fontSize: 16 }} />}
                label={`${orderSectionTasks.filter(t => t.status === "PENDING").length} ${t("Pending")}`}
                sx={{ bgcolor: "#fef3c7", color: "#d97706", fontWeight: 500 }}
              />
            </Stack>
          </Box>

          {/* Tasks Table */}
          <Box sx={{ p: 2 }}>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& th": { bgcolor: "#f8fafc", fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" } }}>
                    <TableCell>#</TableCell>
                    <TableCell>{t("Customer")}</TableCell>
                    <TableCell>{t("Product")}</TableCell>
                    <TableCell align="center">{t("Qty")}</TableCell>
                    <TableCell align="center">{t("Status")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderSectionTasks.map((task, idx) => {
                    const isFinished = task.status === "FINISHED"
                    const isPending = task.status === "PENDING"
                    // Get customer name from order
                    const taskOrder = orders.find(o => o.id === task.orderref)
                    const customerName = taskOrder?.name || task.customername || "-"
                    return (
                      <TableRow
                        key={task.id}
                        sx={{
                          bgcolor: isFinished ? "#f0fdf4" : "transparent",
                          "&:hover": { bgcolor: isFinished ? "#dcfce7" : "#f8fafc" },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" color="#64748b">
                            {idx + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} color="#1e293b" noWrap sx={{ maxWidth: 120 }}>
                            {customerName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="#475569" noWrap sx={{ maxWidth: 150 }}>
                            {task.productname || task.name || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={task.quantity || 1}
                            size="small"
                            sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600, height: 22, minWidth: 32 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={isFinished ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : isPending ? <PendingIcon sx={{ fontSize: 14 }} /> : <PlayArrowIcon sx={{ fontSize: 14 }} />}
                            label={isFinished ? t("Done") : isPending ? t("Pending") : t("Active")}
                            size="small"
                            sx={{
                              bgcolor: isFinished ? "#dcfce7" : isPending ? "#fef3c7" : "#dbeafe",
                              color: isFinished ? "#16a34a" : isPending ? "#d97706" : "#3b82f6",
                              fontWeight: 500,
                              fontSize: "0.7rem",
                              height: 24,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                  {orderSectionTasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="#94a3b8">{t("No tasks found")}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default MuiQuickView
